import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { DateTime } from 'luxon';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { GuidService } from 'ui-components/services/guid-service';
import { Segment } from '../../../../services/analytics-services/segment';
import { LtlFreightTrackingEvents } from '../../tracking-events';
import type { LtlFreightBookingSource } from '../../tracking-events';

import { jobGuidManagerFactory } from '@shipstation/async-client-manager';
import { FreightBookingWizard, isReadyToBook, WIZARD_MODE } from '@shipstation/client-common/components/freight-booking-wizard';
import { FREIGHT_FFP_STATUS_QUOTED, mapFreightFfpStatusId } from '@shipstation/client-common/ltl/freight-ffp-status';
import { reconcileSelection } from '@shipstation/client-common/components/freight-rate-card';
import {
  bookFulfillmentPlan,
  getBookedShipment,
  saveConfiguration,
  saveQuoteToFulfillmentPlan,
} from '@shipstation/client-common/api/ltl.api';
import { createCustomer } from '@shipstation/client-common/api/customers.api';
import {
  buildBookFulfillmentPlanPayload,
  buildFormStateFromFulfillmentPlan,
  buildSaveConfigurationPayload,
  buildSaveQuoteToFulfillmentPlanPayload,
  createEmptyFormState,
  mapDimensionUnit,
  mapWeightUnit,
} from '@shipstation/client-common/ltl/freight-payload-mappers';
import { FREIGHT_INSUFFICIENT_INVENTORY_CODE } from '@shipstation/client-common/ltl/constants';
import { useFreightBookProgress } from '@shipstation/client-common/pusher/hooks/use-freight-book-progress';
import { useFreightFulfillmentPlanQuoteProgress } from '@shipstation/client-common/pusher/hooks/use-freight-fulfillment-plan-quote-progress';
import { useFreightQuoteProgress } from '@shipstation/client-common/pusher/hooks/use-freight-quote-progress';
import { useHandleBookingFormChange } from '@shipstation/client-common/ltl/hooks/use-handle-booking-form-change';
import {
  LTL_PUSHER_TIMEOUT_MS,
  LTL_BOOK_PUSHER_TIMEOUT_MS,
  makeLtlPusherService,
} from '@shipstation/client-common/pusher/ltl-fallback-helpers';
import { NotificationService, NOTIFICATION_TYPE_ERROR, NOTIFICATION_DURATIONS } from 'ui-components/services/notification-service';
import { useSubscribableState, freightQuoteState } from '../../../../subscribable-state';
import { useEnableLtlHazmat } from '../../../../feature-flags/hooks/use-enable-ltl-hazmat';
import { useEnableLtlRegistration } from '../../../../feature-flags/hooks/use-enable-ltl-registration';
import { useCancelFreightFulfillmentPlan } from '../../../../hooks/freight/use-cancel-freight-fulfillment-plan';
import { useFreightAccountActivationFlow } from '../../../../hooks/freight/use-freight-account-activation-flow';
import { useShipFromResolution } from '../../../../hooks/freight/use-ship-from-resolution';
import { useModal } from '../../../../hooks/use-modal';
import { useSellerSetting } from '../../../../hooks/use-seller-setting';
import { SellerSettings } from '../../../../enums/seller-settings/seller-settings';
import { SellerSettingsOptions } from '../../../../enums/seller-settings/seller-settings-options';
import { useMaxZ } from 'ui-components/hooks/services/use-max-z';
import { useModalManagerPortal } from '../../../../portals/hooks';
import { createLookupHash } from '../../../../services/hash-service';
import { PusherService } from '../../../../services/pusher-service';
import Global from '../../../../store/global-selectors';
import { entitiesLoad, entitiesBulkload } from '../../../../store/entities/entities.actions';
import { processEntities } from '../../../../store/entities/entities.bulkload';
import { getFreightGridRequest } from '../../../../store/ltl/ltl.actions';
import { EventSourceStrings } from '../../../../tracking/tracking-constants';
import { useTrackOpenFreightQuote } from '../../../../tracking/use-track-open-freight-quote';
import ShipFromModal from '../../../../ship-from/modals';
import { useGlobalModalLaunchContext } from '../../../../contexts/modal-context/global-modal-context';
import { FreightInventoryWarningModal } from './components/freight-inventory-warning-modal/freight-inventory-warning-modal';
import { freightBookingWizardContainerMessages } from './messages';

import { getMajorUnitType } from '@shipstation/client-common/utils/weight-utils';
import type { BaseWeightUnit } from '@shipstation/client-common/types/weight';
import type { DimensionUnit } from '@shipstation/client-common/types/dimensions';
import type {
  FreightBookingFormState,
  FreightDensityAction,
  WizardStepId,
} from '@shipstation/client-common/components/freight-booking-wizard';
import type { FreightFulfillmentPlanSaveQuoteRequest } from '@shipstation/client-common/types/freight-requests';
import type { AsyncTaskErrorEnvelope } from '@shipstation/client-common/types/common';
import type { BulkLoadResponse } from '@shipstation/client-common/types/bulkload-response';
import type { FreightShipment } from '@shipstation/client-common/types/freight-responses';
import type { FulfillmentPlan } from '@shipstation/client-common/types/fulfillment-plan';
import type { LtlQuote } from '@shipstation/client-common/types/ltl';
import type { AxiosError } from 'axios';
import {
  FreightQuoteTopicStatus,
  FreightQuoteErrorReason,
} from '../../../../orders/order-details/fulfillment-plan/sections/rate-card/actionable/freight-quote-topic';
import type { FreightQuoteTopic } from '../../../../orders/order-details/fulfillment-plan/sections/rate-card/actionable/freight-quote-topic';

// Wires NEW, load-saved-quote, and view-booked modes. After a booking is confirmed
// (or on view-booked mount) the container opens the global BookingConfirmationView modal.

const DEFAULT_ACTIVE_STEPS: readonly WizardStepId[] = ['ship-from'];

type FreightBookingWizardContainerProps =
  | {
      mode?: typeof WIZARD_MODE.NEW;
      isOpen: boolean;
      onClose: () => void;
      seedFormState: FreightBookingFormState;
      seedRates?: LtlQuote[];
      seedSelectedRate?: LtlQuote | null;
      source?: LtlFreightBookingSource;
    }
  | {
      // Launches the wizard directly with an empty form, bypassing the
      // FreightBrowser browse/quote handoff. Renders the presentational wizard
      // in 'new' mode with a fresh empty form state.
      mode: typeof WIZARD_MODE.NEW_DIRECT;
      isOpen: boolean;
      onClose: () => void;
      source?: LtlFreightBookingSource;
    }
  | {
      mode: typeof WIZARD_MODE.LOAD_SAVED_QUOTE;
      isOpen: boolean;
      onClose: () => void;
      fulfillmentPlanId: string;
      source?: LtlFreightBookingSource;
    }
  | {
      mode: typeof WIZARD_MODE.VIEW_BOOKED;
      isOpen: boolean;
      onClose: () => void;
      fulfillmentPlanId: string;
      /** Pins which of the plan's shipments to open. Falls back to the plan's current shipment when omitted. */
      freightShipmentId?: string;
      source?: LtlFreightBookingSource;
    };

export const FreightBookingWizardContainer = (props: FreightBookingWizardContainerProps) => {
  const { isOpen, onClose } = props;
  const mode = props.mode ?? WIZARD_MODE.NEW;
  const source = 'source' in props ? props.source : undefined;
  const fulfillmentPlanId =
    props.mode === WIZARD_MODE.LOAD_SAVED_QUOTE || props.mode === WIZARD_MODE.VIEW_BOOKED ? props.fulfillmentPlanId : undefined;
  const requestedFreightShipmentId = props.mode === WIZARD_MODE.VIEW_BOOKED ? props.freightShipmentId : undefined;
  const intl = useIntl();
  const dispatch = useDispatch();
  const store = useStore();
  const isHazmatEnabled = useEnableLtlHazmat();
  const isLtlRegistrationEnabled = useEnableLtlRegistration();
  const defaultShipFromId = useSelector(Global.selectSellerDefaultShipFromId);
  const freightProviders = useSelector(Global.selectSellerFreightProviders);
  const weightUnit = useSelector(Global.selectSellerDefaultWeightUnit) as BaseWeightUnit;
  const dimensionUnit = useSelector(Global.selectSellerDefaultDimensionUnit) as DimensionUnit;
  const currencyCode = (useSelector(Global.selectSellerDefaultCurrency) as string | undefined) ?? 'USD';
  const states = useSelector(Global.selectReferenceDatagetStates);
  const displayWeightUnit = getMajorUnitType(weightUnit);

  const isQuotedStatus = useSelector((state) =>
    fulfillmentPlanId
      ? mapFreightFfpStatusId(
          (Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId }) as FulfillmentPlan | undefined)?.freightConfiguration
            ?.freightFulfillmentPlanStatusID ?? 0,
        ) === FREIGHT_FFP_STATUS_QUOTED
      : false,
  );
  const isPendingQuote = mode === WIZARD_MODE.LOAD_SAVED_QUOTE && isQuotedStatus;
  const trackOpenFreightQuote = useTrackOpenFreightQuote();

  const cancelFreightFulfillmentPlan = useCancelFreightFulfillmentPlan();

  const hasSeed = mode === WIZARD_MODE.NEW;
  const [formState, setFormState] = useState<FreightBookingFormState>(() => {
    const today = DateTime.now().toFormat('yyyy-MM-dd');
    if (hasSeed) {
      const seedFormState = (props as { seedFormState: FreightBookingFormState }).seedFormState;
      return { ...seedFormState, shipmentDate: seedFormState.shipmentDate || today };
    }
    return {
      ...createEmptyFormState(freightProviders?.[0]?.sellerFreightProviderId, displayWeightUnit, dimensionUnit),
      selectedShipFromId: defaultShipFromId || '',
      shipmentDate: today,
    };
  });
  const [rates, setRates] = useState<LtlQuote[]>(() => (hasSeed ? ((props as { seedRates?: LtlQuote[] }).seedRates ?? []) : []));
  const [isLoadingRates, setIsLoadingRates] = useState(false);
  const [rateError, setRateError] = useState<string | undefined>(undefined);
  const [rateErrorCode, setRateErrorCode] = useState<string | null | undefined>(undefined);
  const [selectedRate, setSelectedRate] = useState<LtlQuote | null>(() =>
    hasSeed ? ((props as { seedSelectedRate?: LtlQuote | null }).seedSelectedRate ?? null) : null,
  );
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isDeletingDraft, setIsDeletingDraft] = useState(false);
  const [isBooking, setIsBooking] = useState(false);
  // Latches true after the initial load-saved-quote hydration so the wizard can be
  // remounted once (see the `key` below) to pick up the pre-completion props. A plain
  // `rates.length > 0` check would wrongly toggle on every re-rate (which clears rates).
  const [hasHydratedSavedQuote, setHasHydratedSavedQuote] = useState(false);
  const { open: openAddShipFrom, close: closeAddShipFrom, isOpen: isAddShipFromOpen } = useModal();
  const { open: openInventoryWarning, close: closeInventoryWarning, isOpen: isInventoryWarningOpen } = useModal();
  const [inventoryWarningMessage, setInventoryWarningMessage] = useState('');
  const shipNoStockSetting = useSellerSetting(SellerSettings.InventoryShipNoStock);
  const { openBookingConfirmation, closeBookingConfirmation } = useGlobalModalLaunchContext() ?? {};
  // Fallback while getConnectedProviderId() waits on the post-registration Redux refresh to land.
  const justRegisteredProviderIdRef = useRef<number | null>(null);
  const modalManagerRef = useModalManagerPortal();
  const zIndex = useMaxZ();

  const formStateRef = useRef(formState);
  formStateRef.current = formState;
  const selectedRateRef = useRef(selectedRate);
  selectedRateRef.current = selectedRate;
  const ratesRef = useRef(rates);
  ratesRef.current = rates;
  // Snapshot of the selection taken right before a re-rate, used to reconcile once
  // the fresh rates have all arrived.
  const priorSelectedRateRef = useRef<LtlQuote | null>(null);
  // Prevents duplicate customer records if the user saves a draft and then books
  // without closing the wizard — customer creation is a one-shot per wizard session.
  const hasCreatedCustomerRef = useRef(false);

  // Tracks the fulfillmentPlanId for the in-progress save, so auto-save updates the
  // correct entity on subsequent calls.
  const fulfillmentPlanIdRef = useRef<string | null>(fulfillmentPlanId ?? null);

  // Guards against setState after the wizard closes/unmounts mid-fetch (load-saved-quote).
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const sourceRef = useRef(source);
  // Set to true after any terminal action (save, book, cancel) so onClose doesn't
  // also fire closed_without_booking.
  const hasFiredTerminalEventRef = useRef(false);
  // Created on each wizard open; threaded into all subsequent freight tracking events
  // to correlate the full booking session.
  const workflowGuidRef = useRef<string>('');

  useEffect(() => {
    if (!isOpen || mode === WIZARD_MODE.VIEW_BOOKED) return;
    workflowGuidRef.current = GuidService.newGuid();
    Segment.trackEvent(LtlFreightTrackingEvents.BOOKING_FORM_OPENED, {
      source: sourceRef.current,
      workflowGuid: workflowGuidRef.current,
    });
  }, [isOpen, mode]);

  // Writing to freightQuoteState keeps the still-mounted rate-card Book action in sync
  // with wizard saves without requiring a separate re-quote round-trip.
  // Harmless no-op for NEW/NEW_DIRECT: no fulfillmentPlanId means no subscribers.
  const [, setFreightQuoteTopic] = useSubscribableState(
    freightQuoteState(fulfillmentPlanId, { status: 'idle' as const, bookableRate: null }),
  ) as [unknown, (value: FreightQuoteTopic) => void];

  const publishCurrentRateToRateCard = useCallback(() => {
    if (selectedRateRef.current) {
      setFreightQuoteTopic({ status: FreightQuoteTopicStatus.Ready, bookableRate: selectedRateRef.current });
    } else {
      setFreightQuoteTopic({ status: FreightQuoteTopicStatus.Error, bookableRate: null, errorReason: FreightQuoteErrorReason.NoMatch });
    }
  }, [setFreightQuoteTopic]);

  // When a re-quote starts, clear the rate-card topic to Loading so it doesn't briefly
  // show a stale bookable rate between the autosave and the fresh quote arriving.
  const setIsLoadingRatesAndSync = useCallback(
    (action: SetStateAction<boolean>) => {
      setIsLoadingRates(action);
      if (action === true) {
        setFreightQuoteTopic({ status: FreightQuoteTopicStatus.Loading, bookableRate: null });
      }
    },
    [setFreightQuoteTopic],
  ) as Dispatch<SetStateAction<boolean>>;

  const getConnectedProviderId = useCallback((): number | undefined => {
    return freightProviders?.[0]?.sellerFreightProviderId ?? undefined;
  }, [freightProviders]);
  const connectedFreightProviderId = freightProviders?.[0]?.freightProviderId ?? null;

  // When mode is load-saved-quote, hydrate form state + rates from the cached Redux
  // FulfillmentPlan entity (available from the grid call).
  useEffect(() => {
    if (!isOpen || mode !== WIZARD_MODE.LOAD_SAVED_QUOTE || !fulfillmentPlanId) return;

    const cached = Global.selectEntitiesFulfillmentPlanById(store.getState(), { fulfillmentPlanId }) as FulfillmentPlan | undefined;
    if (cached) {
      const hydrated = buildFormStateFromFulfillmentPlan(cached, getConnectedProviderId(), displayWeightUnit, dimensionUnit);
      // Sync refs directly before calling triggerRequote so its guards
      // (isFormReadyToQuote, priorSelectedRateRef capture) read hydrated values,
      // not the stale values formStateRef/selectedRateRef still hold this render.
      formStateRef.current = hydrated.formState;
      selectedRateRef.current = hydrated.selectedRate;
      setFormState(hydrated.formState);
      setRates(hydrated.rates);
      setSelectedRate(hydrated.selectedRate);
      // Sync the re-quote snapshot to the hydrated state so the first user edit is
      // compared against real data, not the empty initial form (which would make every
      // edit look like a quote-relevant change and trigger a spurious re-quote).
      resetRequoteSnapshot(hydrated.formState);
      if (hydrated.rates.length > 0) {
        setHasHydratedSavedQuote(true);
        trackOpenFreightQuote();
      }
      if (hydrated.selectedRate !== null && !hydrated.selectedRate.productTransactionId) {
        triggerRequote();
      }
    }
    // fulfillmentPlanId + isOpen + mode drive re-hydration. Callbacks (setters, resetRequoteSnapshot,
    // triggerRequote) are intentionally omitted — their identity changes must not re-trigger hydration.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fulfillmentPlanId, isOpen, mode, trackOpenFreightQuote]);

  // Fetches the fully-booked shipment record and loads it into the entities store so
  // the confirmation view's selector can read it. Mirrors the old context's
  // loadBookedShipmentInternal. Reused by both the post-book flow and view-booked mount.
  const loadBookedShipment = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const data = (await getBookedShipment(id)) as FreightShipment & AsyncTaskErrorEnvelope;
        if (!mountedRef.current) return false;
        if (data.success === false) {
          NotificationService.showGenericFailureNotification(
            undefined,
            data.errors?.[0]?.message ?? intl.formatMessage(freightBookingWizardContainerMessages.loadBookedShipmentFailed),
          );
          return false;
        }
        dispatch(entitiesLoad({ entities: { freightShipments: { [String(data.freightShipmentId)]: data } } }));
        return true;
      } catch (err) {
        if (!mountedRef.current) return false;
        const message =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          intl.formatMessage(freightBookingWizardContainerMessages.loadBookedShipmentFailed);
        NotificationService.showGenericFailureNotification(undefined, message);
        return false;
      }
    },
    [dispatch, intl],
  );

  // In view-booked mode, open the shipment the caller asked for; when it did not name one, fall back to the
  // FulfillmentPlan back-ref. Then render the confirmation view and fetch the full booked record.
  useEffect(() => {
    if (!isOpen || mode !== WIZARD_MODE.VIEW_BOOKED || !fulfillmentPlanId) return;

    const state = store.getState();
    const shipment = Global.selectEntitiesFreightShipmentByFulfillmentPlanId(state, { fulfillmentPlanId }) as FreightShipment | undefined;
    const planShipmentId = shipment?.freightShipmentId != null ? String(shipment.freightShipmentId) : null;
    const resolvedShipmentId = requestedFreightShipmentId ?? planShipmentId;

    if (resolvedShipmentId) {
      openBookingConfirmation?.({
        freightShipmentId: resolvedShipmentId,
        bookingConfirmation: null,
        source: sourceRef.current,
        isLoading: true,
      });
      void loadBookedShipment(resolvedShipmentId).then((loaded) => {
        if (!loaded) {
          closeBookingConfirmation?.();
        } else {
          openBookingConfirmation?.({
            freightShipmentId: resolvedShipmentId,
            bookingConfirmation: null,
            source: sourceRef.current,
            isLoading: false,
          });
        }
        onClose();
      });
    } else {
      NotificationService.showGenericFailureNotification(
        undefined,
        intl.formatMessage(freightBookingWizardContainerMessages.viewBookedShipmentDataMissing),
      );
      onClose();
    }
    // fulfillmentPlanId + requestedFreightShipmentId + isOpen drive the hydration; the setters/loader are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fulfillmentPlanId, requestedFreightShipmentId, isOpen, mode]);

  useEffect(() => {
    if (!rateError) return;
    Segment.trackEvent(LtlFreightTrackingEvents.RATE_ERROR_SHOWN, {
      error_message: rateError,
      workflowGuid: workflowGuidRef.current,
    });
  }, [rateError]);

  useEffect(() => {
    if (!isOpen || mode === WIZARD_MODE.VIEW_BOOKED) return;
    const handleTabClose = () => {
      if (hasFiredTerminalEventRef.current) return;
      Segment.trackEvent(LtlFreightTrackingEvents.BOOKING_CLOSE_WITHOUT_BOOKING, {
        source: sourceRef.current,
        operation: fulfillmentPlanIdRef.current ? 'UPDATE' : 'NEW',
        ...(fulfillmentPlanIdRef.current ? { fulfillment_plan_id: fulfillmentPlanIdRef.current } : {}),
        workflowGuid: workflowGuidRef.current,
      });
    };
    window.addEventListener('beforeunload', handleTabClose);
    return () => {
      window.removeEventListener('beforeunload', handleTabClose);
    };
  }, [isOpen, mode]);

  const pendingQuoteGuidsRef = useRef<Set<string>>(new Set());
  const pendingBookGuidsRef = useRef<Set<string>>(new Set());

  const quotePusherService = useMemo(
    () => makeLtlPusherService(pendingQuoteGuidsRef, PusherService.bindToEvent.bind(PusherService), jobGuidManagerFactory),
    [],
  );
  const fulfillmentPlanQuotePusherService = useMemo(
    () => makeLtlPusherService(pendingQuoteGuidsRef, PusherService.bindToEvent.bind(PusherService), jobGuidManagerFactory),
    [],
  );
  const bookPusherService = useMemo(
    () => makeLtlPusherService(pendingBookGuidsRef, PusherService.bindToEvent.bind(PusherService), jobGuidManagerFactory),
    [],
  );

  const quoteProgressCallbacks = {
    onQuotes: (quotes: LtlQuote[]) => {
      // Append to the ref synchronously so onComplete (which can fire in the same
      // tick as the final onQuotes, before a re-render refreshes ratesRef) reconciles
      // against the freshly-arrived rates instead of a stale empty array.
      const next = [...ratesRef.current, ...quotes];
      ratesRef.current = next;
      setRates(next);
    },
    onError: (message: string, code?: string | null) => {
      setRateError(message);
      setRateErrorCode(code ?? null);
      setIsLoadingRates(false);
    },
    onComplete: () => {
      setIsLoadingRates(false);
      const reconciled = reconcileSelection(priorSelectedRateRef.current, ratesRef.current, formStateRef.current.insurance.isInsured);
      setSelectedRate(reconciled);
      // If this is a re-quote in LOAD_SAVED_QUOTE mode and rates now exist, mark hydration as complete
      // so the wizard remounts with proper step initialization.
      if (mode === WIZARD_MODE.LOAD_SAVED_QUOTE && !hasHydratedSavedQuote && ratesRef.current.length > 0) {
        setHasHydratedSavedQuote(true);
      }
    },
  };

  const { startListening: startQuoteListening, cancelListening: cancelQuoteListening } = useFreightQuoteProgress({
    pusherService: quotePusherService,
    pendingGuidsRef: pendingQuoteGuidsRef,
    ...quoteProgressCallbacks,
    timeoutMs: LTL_PUSHER_TIMEOUT_MS,
  });

  const { startListening: startFulfillmentPlanQuoteListening, cancelListening: cancelFulfillmentPlanQuoteListening } =
    useFreightFulfillmentPlanQuoteProgress({
      pusherService: fulfillmentPlanQuotePusherService,
      pendingGuidsRef: pendingQuoteGuidsRef,
      ...quoteProgressCallbacks,
      timeoutMs: LTL_PUSHER_TIMEOUT_MS,
    });

  const { startListening: startBookListening, cancelListening: cancelBookListening } = useFreightBookProgress({
    pusherService: bookPusherService,
    pendingGuidsRef: pendingBookGuidsRef,
    onConfirmation: (confirmation) => {
      hasFiredTerminalEventRef.current = true;
      Segment.trackEvent(LtlFreightTrackingEvents.BOOKING_BOOK, {
        source: sourceRef.current,
        operation: fulfillmentPlanIdRef.current ? 'UPDATE' : 'NEW',
        ...(fulfillmentPlanIdRef.current ? { fulfillment_plan_id: fulfillmentPlanIdRef.current } : {}),
        is_saving_new_customer: formStateRef.current.shouldSaveAsNewCustomer ?? false,
        workflowGuid: workflowGuidRef.current,
      });
      dispatch(getFreightGridRequest());
      if (confirmation.freightShipmentId) {
        const id = String(confirmation.freightShipmentId);
        // Defer setIsBooking(false) until the fetch settles — prevents a double-click during the async gap.
        void loadBookedShipment(id).then((loaded) => {
          setIsBooking(false);
          if (!mountedRef.current) return;
          if (loaded) {
            openBookingConfirmation?.({
              freightShipmentId: id,
              bookingConfirmation: confirmation,
              source: sourceRef.current,
            });
          }
          onClose();
        });
      } else {
        setIsBooking(false);
      }
    },
    onError: (message, code) => {
      // Insufficient inventory in warn mode: let the seller confirm and re-book with an
      // override instead of surfacing a dead-end error. ('allow' never fails here; 'error'
      // blocks server-side, so fall through to the generic error notification below.)
      if (code === FREIGHT_INSUFFICIENT_INVENTORY_CODE && shipNoStockSetting === SellerSettingsOptions.warnShip.value) {
        setInventoryWarningMessage(message);
        openInventoryWarning();
        setIsBooking(false);
        return;
      }
      NotificationService.showNotification({
        title: intl.formatMessage(freightBookingWizardContainerMessages.bookFailed),
        content: message,
        duration: NOTIFICATION_DURATIONS.LONG,
        notificationType: NOTIFICATION_TYPE_ERROR,
      });
      setIsBooking(false);
    },
    // eslint-disable-next-line no-empty-function
    onComplete: () => {},
    timeoutMs: LTL_BOOK_PUSHER_TIMEOUT_MS,
  });

  const { shipFromOptions, shipFromAddress } = useShipFromResolution(formState.selectedShipFromId);

  const buildSaveQuotePayload = useCallback((): FreightFulfillmentPlanSaveQuoteRequest => {
    const sellerFreightProviderId = getConnectedProviderId() ?? justRegisteredProviderIdRef.current ?? undefined;
    return buildSaveQuoteToFulfillmentPlanPayload(
      fulfillmentPlanIdRef.current,
      formStateRef.current,
      ratesRef.current,
      selectedRateRef.current,
      mapWeightUnit(weightUnit),
      mapDimensionUnit(dimensionUnit),
      sellerFreightProviderId,
    );
  }, [dimensionUnit, getConnectedProviderId, weightUnit]);

  const onQuoteSuccess = useCallback(
    (envelope: AsyncTaskErrorEnvelope, jobGuids: string[]) => {
      if (!mountedRef.current) return;
      if (envelope.success === false) {
        setRateError(envelope.errors?.[0]?.message ?? intl.formatMessage(freightBookingWizardContainerMessages.failedToRetrieveQuotes));
        setIsLoadingRates(false);
      }
      if (jobGuids.length === 0) {
        setIsLoadingRates(false);
      }
    },
    [intl],
  );

  const onQuoteError = useCallback(
    (err: AxiosError) => {
      if (!mountedRef.current) return;
      const responseData = (err as { response?: { data?: unknown } })?.response?.data;
      const message =
        (typeof responseData === 'string' ? responseData : null) ??
        (responseData as { Message?: string })?.Message ??
        (err instanceof Error ? err.message : null) ??
        intl.formatMessage(freightBookingWizardContainerMessages.rateFetchError);
      setRateError(message);
      setIsLoadingRates(false);
    },
    [intl],
  );

  const getFulfillmentPlan = useCallback(() => {
    if (!fulfillmentPlanIdRef.current) {
      return;
    }
    const fp = Global.selectEntitiesFulfillmentPlanById(store.getState(), { fulfillmentPlanId: fulfillmentPlanIdRef.current }) as
      | FulfillmentPlan
      | undefined;

    return fp;
  }, [store]);

  const onSaveConfigurationSuccess = useCallback(
    (result: BulkLoadResponse) => {
      if (!mountedRef.current) return;
      if (result?.fulfillmentPlans?.length) {
        fulfillmentPlanIdRef.current = result.fulfillmentPlans[0].fulfillmentPlanId;
        dispatch(
          entitiesBulkload({
            ...processEntities(result),
            freightShipments: createLookupHash(result.freightShipments, 'freightShipmentId'),
          }),
        );
      }

      // Keep the rate-card Book action in sync with wizard autosave updates.
      publishCurrentRateToRateCard();
    },
    [dispatch, publishCurrentRateToRateCard],
  );

  const onQuoteFulfillmentPlanSuccess = useCallback(
    (envelope: AsyncTaskErrorEnvelope, jobGuids: string[]) => {
      if (!mountedRef.current) return;
      if (envelope.success === false) {
        setRateError(envelope.errors?.[0]?.message ?? intl.formatMessage(freightBookingWizardContainerMessages.failedToRetrieveQuotes));
        setIsLoadingRates(false);
        return;
      }
      if (jobGuids.length === 0) {
        setIsLoadingRates(false);
      }
    },
    [intl],
  );

  const onQuoteFulfillmentPlanError = useCallback(
    (err: AxiosError) => {
      if (!mountedRef.current) return;
      const responseData = (err as { response?: { data?: unknown } })?.response?.data;
      const message =
        (typeof responseData === 'string' ? responseData : null) ??
        (responseData as { Message?: string })?.Message ??
        (err instanceof Error ? err.message : null) ??
        intl.formatMessage(freightBookingWizardContainerMessages.rateFetchError);
      setRateError(message);
      setIsLoadingRates(false);
    },
    [intl],
  );

  const {
    onChange: handleChange,
    resetRequoteSnapshot,
    triggerRequote,
  } = useHandleBookingFormChange({
    mode,
    fulfillmentPlanIdRef,
    getConnectedProviderId,
    setIsLoadingRates: setIsLoadingRatesAndSync,
    setRateError,
    setRateErrorCode,
    setRates,
    ratesRef,
    priorSelectedRateRef,
    selectedRateRef,
    cancelQuoteListening,
    mountedRef,
    startQuoteListening,
    getFulfillmentPlan,
    weightUnit,
    dimensionUnit,
    formStateRef,
    onSaveConfigurationSuccess,
    cancelFulfillmentPlanQuoteListening,
    startFulfillmentPlanQuoteListening,
    onQuoteSuccess,
    onQuoteError,
    onQuoteFulfillmentPlanSuccess,
    onQuoteFulfillmentPlanError,
  });

  const handleFormChange = useCallback(
    (next: FreightBookingFormState) => {
      setFormState(next);
      handleChange(next);
    },
    [handleChange],
  );

  // triggerRequote takes an optional override provider id — wrapped so the refresh
  // button's click event never gets passed through as that argument.
  const handleRefresh = useCallback(() => {
    triggerRequote();
  }, [triggerRequote]);

  const handleAddShipFromSuccess = useCallback(
    (result: object) => {
      const newShipFrom = result as { shipFromId: string };
      closeAddShipFrom();
      // ShipFromContextProvider dispatches addNewShipFrom(result) before calling onSuccess,
      // so sellerData.shipFroms already includes the new entry — no fetchShipFroms needed.
      handleFormChange({ ...formStateRef.current, selectedShipFromId: newShipFrom.shipFromId });
      Segment.trackEvent(LtlFreightTrackingEvents.ADD_SHIP_FROM, {
        workflowGuid: workflowGuidRef.current,
      });
      NotificationService.showGenericSuccessNotification(
        undefined,
        intl.formatMessage(freightBookingWizardContainerMessages.addShipFromSuccess),
      );
    },
    [closeAddShipFrom, handleFormChange, intl],
  );

  const handleCustomerSelect = useCallback(() => {
    Segment.trackEvent(LtlFreightTrackingEvents.EXISTING_CUSTOMER_USED, {
      workflowGuid: workflowGuidRef.current,
    });
  }, []);

  const handleDensityAction = useCallback((action: FreightDensityAction) => {
    Segment.trackEvent(LtlFreightTrackingEvents.CALC_DENSITY_CLICKED, {
      action,
      workflowGuid: workflowGuidRef.current,
    });
  }, []);

  const handleCommodityPresetSelect = useCallback((presetId: number) => {
    Segment.trackEvent(LtlFreightTrackingEvents.EXISTING_COMMODITY_PRESET_USED, {
      preset_id: presetId,
      workflowGuid: workflowGuidRef.current,
    });
  }, []);

  const maybeCreateCustomer = useCallback(
    async (state: FreightBookingFormState) => {
      if (!state.shouldSaveAsNewCustomer || hasCreatedCustomerRef.current) return;
      const addr = state.destinationAddress;
      try {
        await createCustomer({
          name: addr.name,
          email: addr.email,
          company: addr.company ?? '',
          street1: addr.line1,
          street2: addr.line2,
          city: addr.city,
          state: addr.state,
          postalCode: addr.postalCode,
          countryCode: addr.countryCode,
          phone: addr.phone,
        });
        hasCreatedCustomerRef.current = true;
        NotificationService.showGenericSuccessNotification(
          undefined,
          intl.formatMessage(freightBookingWizardContainerMessages.createCustomerSuccess),
        );
      } catch {
        NotificationService.showGenericFailureNotification(
          undefined,
          intl.formatMessage(freightBookingWizardContainerMessages.createCustomerFailed),
        );
      }
    },
    [intl],
  );

  const handleSaveDraft = useCallback(async (): Promise<boolean> => {
    const payload = buildSaveQuotePayload();

    void maybeCreateCustomer(formStateRef.current);
    setIsSavingDraft(true);
    try {
      const operation = fulfillmentPlanIdRef.current ? 'UPDATE' : 'NEW';
      const result = (await saveQuoteToFulfillmentPlan(payload)) as BulkLoadResponse;
      if (result?.fulfillmentPlans?.length) {
        fulfillmentPlanIdRef.current = result.fulfillmentPlans[0].fulfillmentPlanId;
      }
      hasFiredTerminalEventRef.current = true;
      Segment.trackEvent(LtlFreightTrackingEvents.BOOKING_SAVE_DRAFT, {
        source: sourceRef.current,
        operation,
        ...(fulfillmentPlanIdRef.current ? { fulfillment_plan_id: fulfillmentPlanIdRef.current } : {}),
        workflowGuid: workflowGuidRef.current,
      });
      dispatch(getFreightGridRequest());
      // Publish to freightQuoteState so the rate-card and Book button update immediately without a re-quote.
      publishCurrentRateToRateCard();
      NotificationService.showGenericSuccessNotification(
        undefined,
        intl.formatMessage(freightBookingWizardContainerMessages.saveQuoteSuccess),
      );
      onClose();
      return true;
    } catch (err) {
      const errorMessage =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        intl.formatMessage(freightBookingWizardContainerMessages.saveQuoteFailed);
      NotificationService.showGenericFailureNotification(undefined, errorMessage);
      return false;
    } finally {
      setIsSavingDraft(false);
    }
  }, [buildSaveQuotePayload, dispatch, intl, maybeCreateCustomer, onClose, publishCurrentRateToRateCard]);

  const handleDeleteDraft = useCallback(async () => {
    const id = fulfillmentPlanIdRef.current;
    if (!id) return;
    setIsDeletingDraft(true);
    const didSucceed = await cancelFreightFulfillmentPlan([id]);
    if (didSucceed) {
      hasFiredTerminalEventRef.current = true;
      Segment.trackEvent(LtlFreightTrackingEvents.BOOKING_DELETE, {
        source: sourceRef.current,
        fulfillment_plan_id: id,
        workflowGuid: workflowGuidRef.current,
      });
      dispatch(getFreightGridRequest());
      onClose();
    } else {
      NotificationService.showGenericFailureNotification(
        undefined,
        intl.formatMessage(freightBookingWizardContainerMessages.deleteDraftFailed),
      );
    }
    if (mountedRef.current) setIsDeletingDraft(false);
  }, [cancelFreightFulfillmentPlan, dispatch, intl, onClose]);

  const continueAfterProviderConnected = useCallback(
    async (sellerFreightProviderId: number) => {
      justRegisteredProviderIdRef.current = sellerFreightProviderId;
      const updatedFormState = { ...formStateRef.current, sellerFreightProviderID: sellerFreightProviderId };
      formStateRef.current = updatedFormState;
      setFormState(updatedFormState);

      if (fulfillmentPlanIdRef.current) {
        const fp = getFulfillmentPlan();
        const savePayload = buildSaveConfigurationPayload(
          fulfillmentPlanIdRef.current,
          formStateRef.current,
          null,
          ratesRef.current,
          mapWeightUnit(weightUnit),
          mapDimensionUnit(dimensionUnit),
          fp?.freightConfiguration?.freightFulfillmentPlanStatusID ?? null,
          fp?.freightConfiguration?.freightNumber ?? null,
        );
        if (savePayload) {
          try {
            const result = await saveConfiguration(fulfillmentPlanIdRef.current, savePayload);
            onSaveConfigurationSuccess(result);
          } catch (err) {
            const errorMessage =
              (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
              intl.formatMessage(freightBookingWizardContainerMessages.bookFailed);
            NotificationService.showGenericFailureNotification(undefined, errorMessage);
            // Don't requote against a configuration that failed to save.
            return;
          }
        }
      }

      triggerRequote(sellerFreightProviderId);
    },
    [getFulfillmentPlan, weightUnit, dimensionUnit, intl, onSaveConfigurationSuccess, triggerRequote],
  );

  // WwexRegistrationContainer and ConnectFreightProviderModalContextProvider both resolve a
  // real provider id themselves (awaiting the fresh provider list when their own success
  // callback doesn't hand one back directly) before calling this — so a null id here means
  // registration genuinely didn't produce a connectable account, not "not resolved yet".
  const handleRegistrationSuccess = useCallback(
    (sellerFreightProviderId: number | null) => {
      setSelectedRate(null);

      if (!sellerFreightProviderId) {
        NotificationService.showGenericFailureNotification(
          undefined,
          intl.formatMessage(freightBookingWizardContainerMessages.noProviderConnected),
        );
        return;
      }

      void continueAfterProviderConnected(sellerFreightProviderId);
    },
    [continueAfterProviderConnected, intl],
  );

  const {
    openChooser: openFreightAccountChooser,
    openConnectOnlyChooser: openFreightAccountConnectOnlyChooser,
    freightAccountActivationModals,
    hasJustConnectedProvider,
  } = useFreightAccountActivationFlow(EventSourceStrings.FREIGHT_BOOKING_WIZARD, handleRegistrationSuccess, handleSaveDraft);

  const shouldShowConnectAccountBanner =
    isLtlRegistrationEnabled && mode !== WIZARD_MODE.VIEW_BOOKED && !getConnectedProviderId() && !justRegisteredProviderIdRef.current;

  const handleBook = useCallback(
    async (overrideInventoryWarning = false) => {
      // Booking requires the seller's own registered WWEX account; house-account rates are display-only.
      const requiresRegistrationBeforeBooking =
        isLtlRegistrationEnabled && !getConnectedProviderId() && !justRegisteredProviderIdRef.current && !hasJustConnectedProvider();
      if (requiresRegistrationBeforeBooking) {
        openFreightAccountChooser();
        return;
      }

      const selected = selectedRateRef.current;
      if (!selected || !isReadyToBook(formStateRef.current, { selectedRate: selected })) return;

      setIsBooking(true);
      cancelBookListening();
      void maybeCreateCustomer(formStateRef.current);

      const current = formStateRef.current;

      // If no fulfillment plan exists yet (e.g. the wizard was opened without auto-save
      // having fired), create one first so the book endpoint has a plan to act on.
      if (!fulfillmentPlanIdRef.current) {
        const savePayload = buildSaveQuotePayload();
        if (!savePayload) {
          setIsBooking(false);
          return;
        }
        try {
          const result = (await saveQuoteToFulfillmentPlan(savePayload)) as BulkLoadResponse;
          if (result?.fulfillmentPlans?.length) {
            fulfillmentPlanIdRef.current = result.fulfillmentPlans[0].fulfillmentPlanId;
            dispatch(getFreightGridRequest());
          }
        } catch (err) {
          const errorMessage =
            (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
            intl.formatMessage(freightBookingWizardContainerMessages.bookFailed);
          NotificationService.showGenericFailureNotification(undefined, errorMessage);
          setIsBooking(false);
          return;
        }
      } else {
        const fp = getFulfillmentPlan();
        const savePayload = buildSaveConfigurationPayload(
          fulfillmentPlanIdRef.current,
          formStateRef.current,
          selectedRateRef.current,
          ratesRef.current,
          mapWeightUnit(weightUnit),
          mapDimensionUnit(dimensionUnit),
          fp?.freightConfiguration?.freightFulfillmentPlanStatusID ?? null,
          fp?.freightConfiguration?.freightNumber ?? null,
        );
        if (!savePayload) {
          NotificationService.showGenericFailureNotification(
            undefined,
            intl.formatMessage(freightBookingWizardContainerMessages.bookFailed),
          );
          setIsBooking(false);
          return;
        }
        try {
          const result = await saveConfiguration(fulfillmentPlanIdRef.current, savePayload);
          onSaveConfigurationSuccess(result);
        } catch (err) {
          const errorMessage =
            (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
            intl.formatMessage(freightBookingWizardContainerMessages.bookFailed);
          NotificationService.showGenericFailureNotification(undefined, errorMessage);
          setIsBooking(false);
          return;
        }
      }

      if (!fulfillmentPlanIdRef.current) {
        NotificationService.showGenericFailureNotification(
          undefined,
          intl.formatMessage(freightBookingWizardContainerMessages.bookSuccessButNoFulfillmentPlan),
        );
        setIsBooking(false);
        return;
      }

      const payload = buildBookFulfillmentPlanPayload(
        fulfillmentPlanIdRef.current,
        current,
        selected.productTransactionId,
        overrideInventoryWarning,
      );

      try {
        const data = (await bookFulfillmentPlan(payload)) as AsyncTaskErrorEnvelope;
        if (data.success === false) {
          NotificationService.showGenericFailureNotification(
            undefined,
            data.errors?.[0]?.message ?? intl.formatMessage(freightBookingWizardContainerMessages.bookFailed),
          );
          setIsBooking(false);
          return;
        }
        const jobGuids: string[] = (data.jobGuids ?? data.JobGuids ?? []) as string[];
        startBookListening(jobGuids);
        // The Pusher 'freight-book' event (useFreightBookProgress.onConfirmation) finishes the flow.
      } catch (err) {
        const responseData = (err as { response?: { data?: unknown } })?.response?.data;
        const errorMessage =
          typeof responseData === 'string'
            ? responseData
            : ((responseData as { message?: string })?.message ?? intl.formatMessage(freightBookingWizardContainerMessages.bookFailed));
        NotificationService.showGenericFailureNotification(undefined, errorMessage);
        setIsBooking(false);
      }
    },
    [
      isLtlRegistrationEnabled,
      getConnectedProviderId,
      hasJustConnectedProvider,
      openFreightAccountChooser,
      buildSaveQuotePayload,
      cancelBookListening,
      intl,
      maybeCreateCustomer,
      startBookListening,
      dispatch,
      weightUnit,
      dimensionUnit,
      getFulfillmentPlan,
      onSaveConfigurationSuccess,
    ],
  );

  const handleClose = useCallback(() => {
    if (!hasFiredTerminalEventRef.current && mode !== WIZARD_MODE.VIEW_BOOKED) {
      Segment.trackEvent(LtlFreightTrackingEvents.BOOKING_CLOSE_WITHOUT_BOOKING, {
        source: sourceRef.current,
        operation: fulfillmentPlanIdRef.current ? 'UPDATE' : 'NEW',
        ...(fulfillmentPlanIdRef.current ? { fulfillment_plan_id: fulfillmentPlanIdRef.current } : {}),
        workflowGuid: workflowGuidRef.current,
      });
    }
    onClose();
  }, [mode, onClose]);

  // The wizard's book Button forwards its click event to onBook; wrap so the event
  // object never leaks in as a truthy `overrideInventoryWarning`.
  const handleBookInitial = useCallback(() => {
    void handleBook(false);
  }, [handleBook]);

  const handleBookWithOverride = useCallback(() => {
    closeInventoryWarning();
    void handleBook(true);
  }, [closeInventoryWarning, handleBook]);

  // For load-saved-quote, pre-complete steps 1-3 (port of the old reducer's "mark all
  // steps before quotes complete" intent) once the saved quote has hydrated with rates.
  // The removed 'quotes' step means additional-details becomes the landing step.
  const initialCompletedSteps: Partial<Record<WizardStepId, boolean>> | undefined = hasHydratedSavedQuote
    ? { 'ship-from': true, 'ship-to': true, 'handling-units': true }
    : undefined;
  const initialActiveStep: WizardStepId | undefined = hasHydratedSavedQuote ? 'additional-details' : undefined;

  return (
    <>
      {mode !== WIZARD_MODE.VIEW_BOOKED && (
        <FreightBookingWizard
          // The wizard reads initialCompletedSteps/initialActiveStep only in its lazy
          // useState initializers (mount-time). In load-saved-quote mode hydration runs in
          // an effect AFTER mount, so we remount the wizard once rates hydrate to re-run
          // those initializers with the populated pre-completion props. NEW mode keeps a
          // stable key (and so does an empty saved quote with no rates).
          key={hasHydratedSavedQuote ? 'hydrated' : 'initial'}
          isOpen={isOpen}
          mode={mode}
          onClose={handleClose}
          formState={formState}
          onFormChange={handleFormChange}
          onAddShipFrom={openAddShipFrom}
          onCommodityPresetSelect={handleCommodityPresetSelect}
          onCustomerSelect={handleCustomerSelect}
          onDensityAction={handleDensityAction}
          shipFromOptions={shipFromOptions}
          states={states}
          displayWeightUnit={displayWeightUnit}
          displayDimensionUnit={dimensionUnit}
          currencyCode={currencyCode}
          rates={rates}
          isLoadingRates={isLoadingRates}
          rateError={rateError}
          rateErrorCode={rateErrorCode}
          connectedFreightProviderId={connectedFreightProviderId}
          shouldShowConnectAccountBanner={shouldShowConnectAccountBanner}
          onConnectAccount={openFreightAccountConnectOnlyChooser}
          selectedRate={selectedRate}
          onSelectRate={setSelectedRate}
          onRefresh={handleRefresh}
          shipFromAddress={shipFromAddress}
          destinationAddress={formState.destinationAddress}
          onSaveDraft={handleSaveDraft}
          onBook={handleBookInitial}
          isSavingDraft={isSavingDraft}
          isBooking={isBooking}
          onDeleteDraft={handleDeleteDraft}
          isDeletingDraft={isDeletingDraft}
          canDeleteDraft={isPendingQuote}
          initialCompletedSteps={initialCompletedSteps}
          initialActiveSteps={initialActiveStep ? [initialActiveStep] : DEFAULT_ACTIVE_STEPS}
          isHazmatEnabled={isHazmatEnabled}
          portalRef={modalManagerRef ?? null}
          zIndex={zIndex}
        />
      )}
      {isAddShipFromOpen && (
        <ShipFromModal onClose={closeAddShipFrom} onSuccess={handleAddShipFromSuccess} source={EventSourceStrings.LTL_FREIGHT_WIZARD} />
      )}
      {isInventoryWarningOpen && (
        <FreightInventoryWarningModal
          message={inventoryWarningMessage}
          onContinue={handleBookWithOverride}
          onClose={closeInventoryWarning}
        />
      )}
      {isLtlRegistrationEnabled && freightAccountActivationModals}
    </>
  );
};
